import { Fragment, useEffect, useReducer, type MouseEvent, type ReactElement } from "react";
import { BODY_TYPE } from "odyssey-shared";
import { HoverWords } from "../bar/hover-words.tsx";
import { Button, Glyph, HudFrame, KIT_METRICS, KeyGlyph, centreOffset, logical } from "../../ui/kit/index.ts";
import { useKeyLabel } from "../controls/use-key.ts";
import { PAGE_RULES } from "../rules.ts";
import { flagHints, flagToggleView, toggleDelays, type FlagHint, type ToggleInput } from "./flag-toggle.ts";
import { say } from "../../words.ts";
import { ownNow } from "../own-clock.ts";

/**
 * The flag toggle's panel (ADR-076 decision 6; the design's `flag-toggle` board): under the player card, a HUD frame
 * exactly the card's width (ADR-076's amendment of 2026-09-28: the card's content width in the same kit frame, so both
 * edges line up, where the design drew 150 under a card of 132) holding, in a column 3 apart, the countdown bar while a
 * switch counts, the line (with the lock glyph when the button is locked), and one full-width button. It shows what
 * `flagToggleView` makes of the server's last word and the page clock, and a timer wakes it when the seconds it shows
 * change, so nothing runs on the animation frame. The button only asks (`onRequest`); the panel moves when `pvpSwitch`
 * answers. Under the button a row of key hints names what the keys do in the panel's state (ADR-076's amendment of
 * 2026-10-01 and its note of 2026-10-03, `flagHints`): U to switch and Esc to close where the button offers a switch,
 * Enter to cancel (in a panel U opened) and U to close while one counts, U to close while it is locked, as
 * `ConfirmDialog`'s row does, classed `ot-keyhint` for the design's touch mode to hide.
 */

/**
 * The gap between the panel's rows and between the card and the panel.
 */
const PANEL_GAP = 3;

/**
 * The countdown bar's height, the design's.
 */
const BAR_HEIGHT = 3;

/**
 * The switch's delays in whole seconds, from the page's rules.
 */
const DELAYS = toggleDelays(PAGE_RULES.constants);

/**
 * Keeps a pointer press on the panel's button from taking focus from the game, as the menu button does.
 *
 * @param event The press.
 */
function keepFocus(event: MouseEvent<HTMLElement>): void {
  event.preventDefault();
}

/**
 * The key hint row under the button: each hint's key glyph and its words, the words set in the middle of the key
 * glyph's height. It repeats what the keys do, so it is hidden from assistive technology, as `ConfirmDialog`'s row is.
 *
 * @param props The `hints`, in the row's order.
 * @returns The row.
 */
function KeyHints({ hints }: { readonly hints: readonly FlagHint[] }): ReactElement {
  const words = { marginTop: logical(centreOffset(KIT_METRICS.key.height, BODY_TYPE.caption.lineHeight)) };
  return (
    <span className="ot-hud-toggle__hints ot-keyhint" data-hud="flag-toggle-hints" aria-hidden="true" style={{ gap: logical(KIT_METRICS.dialog.hintItemGap) }}>
      {hints.map((hint) => (
        <Fragment key={hint.key}>
          <KeyGlyph>{hint.key}</KeyGlyph>
          <span className="ot-hud-toggle__hint" style={words}>
            {hint.words}
          </span>
        </Fragment>
      ))}
    </span>
  );
}

/**
 * The panel.
 *
 * @param props The player card's content `width` in logical pixels, which the panel's content takes, everything
 *   `flagToggleView` reads but the clock, whether a key opened the panel (`byKey`, which makes Enter live and the hint
 *   row name it), and `onRequest`, which the button calls with the preference it asks for (the gallery gives none).
 * @returns The panel.
 */
export function FlagTogglePanel({ width, state, byKey, onRequest }: { readonly width: number; readonly state: Omit<ToggleInput, "nowMs">; readonly byKey: boolean; readonly onRequest: ((wanted: number) => void) | undefined }): ReactElement {
  const [, wake] = useReducer((count: number) => count + 1, 0);
  const panelKey = useKeyLabel("flag");
  const view = flagToggleView({ ...state, nowMs: ownNow() }, DELAYS);
  useEffect(() => {
    if (view.changesInMs === null) {
      return undefined;
    }
    const timer = setTimeout(wake, Math.max(0, view.changesInMs));
    return () => clearTimeout(timer);
  }, [view.changesInMs, view.line]);
  const fill = view.progress === null ? 0 : Math.floor(((width - 2) * view.progress.value) / view.progress.max);
  return (
    <div className="ot-hud-toggle" data-hud="flag-toggle" data-state={view.state} role="group" aria-label={say("hud.flag.panel")} style={{ marginTop: logical(PANEL_GAP) }} onMouseDown={keepFocus}>
      <HudFrame>
        <span className="ot-hud-toggle__column" style={{ width: logical(width), gap: logical(PANEL_GAP) }}>
          {view.progress === null ? null : (
            <span className="ot-hud-bar ot-hud-toggle__bar" data-hud="flag-toggle-bar" role="progressbar" aria-label={view.line} aria-valuemin={0} aria-valuemax={view.progress.max} aria-valuenow={view.progress.value} style={{ width: logical(width), height: logical(BAR_HEIGHT) }}>
              <span className="ot-hud-bar__fill" data-fill={fill} style={{ width: logical(fill) }} />
            </span>
          )}
          <span className={`ot-hud-toggle__line${view.warning ? " is-warning" : ""}`} data-hud="flag-toggle-line" style={{ gap: logical(PANEL_GAP) }}>
            {view.locked ? <Glyph name="lock" /> : null}
            <span>{view.line}</span>
          </span>
          {view.locked ? (
            <HoverWords words={view.line} block>
              <Button fullWidth disabled>
                {view.button}
              </Button>
            </HoverWords>
          ) : (
            <Button fullWidth {...(onRequest === undefined ? {} : { onClick: () => onRequest(view.wanted) })}>
              {view.button}
            </Button>
          )}
          <KeyHints hints={flagHints(view, byKey, panelKey)} />
        </span>
      </HudFrame>
    </div>
  );
}
